import { useCallback, useMemo } from 'react';
import { mergeWithDefaultsForView } from '../config/fieldVisibility';

/**
 * useViewSwitcherProps
 * 
 * Assembles all props needed for the ViewSwitcher component.
 * Reduces prop assembly clutter in the main SprintDetail component.
 */
const useViewSwitcherProps = ({
  // Toolbar
  toolbar,
  flowchartRef,
  
  // Data
  tasks,
  statuses,
  sprint,
  localSprint,
  nested,
  allSprints = [],
  
  // Settings
  cardDisplaySettingsPerView,
  swimlaneSetting,
  sortSettingsPerView,
  filtersPerView,
  showArchived,
  archivedCount,
  allTags,
  
  // View state
  todoViewMode,
  setTodoViewMode,
  todoSwimlaneGroupBy,
  setTodoSwimlaneGroupBy,
  // New: Todo swimlane setting (same as Kanban)
  todoSwimlane,
  setTodoSwimlane,
  // New: Show nested items toggle
  todoShowNestedItems,
  setTodoShowNestedItems,
  
  // Actions
  actions,
  handlers,
  
  // Undo/Redo
  undo,
  redo,
  canUndo,
  canRedo,
  undoStack,
  redoStack,
  
  // State setters
  setIsArtifactSummaryOpen,
  setIsHistoryOpen,
  isHistoryOpen,
  handleFiltersChange,
  
  // Handlers
  handleAddKanbanColumn,
  handleCardDisplaySettingsChange,
  handleSwimlaneSettingChange,
  handleSortSettingChange,
  handleToggleArchived,
  
  // Actionable only filter
  showActionableOnly,
  handleToggleActionableOnly,
  
  // Nested campaigns visibility filter
  showNestedCampaigns,
  setShowNestedCampaigns,
  
  // TodoList modal trigger ref (from useTodoListProps)
  todoModalTriggerRef,
  
  // Multi-campaign mode
  isMultiCampaignMode = false,
  activeView = null,
  multiCampaignCampaigns = [],
  onOpenCampaignPicker,
  /** Flowchart Tidy / layout in progress — drives toolbar loading state */
  isTidyToolbarBusy = false,
  // HUD visibility settings
  flowViewVisibility = {},
  onFlowViewVisibilityChange,
  // FOKUS mode for Kanban and Table views
  kanbanFokus,
  tableFokus,
  // FOKUS mode for Gantt, Todo and Neural views
  ganttFokus,
  todoFokus,
  neuralFokus,
  // Table column visibility
  tableColumnVisibility,
  onTableColumnVisibilityChange,
  // Todo element visibility
  todoElementVisibility,
  onTodoElementVisibilityChange,
  // Gantt swimlane setting (same as Kanban)
  ganttSwimlane,
  setGanttSwimlane,
  // Table swimlane setting (same as Kanban)
  tableSwimlane,
  setTableSwimlane,
  // Custom views
  customViews = [],
  activeCustomViewId = null,
  onAddCustomView,
  onSelectCustomView,
  onRenameCustomView,
  onDuplicateCustomView,
  onDeleteCustomView,
  onReorderViews,
  // Custom fields (campaign-level definitions)
  customFields = [],
  onManageCustomFields,
  /** Clear decision answers and set missions to To Do (current campaign tree or multi-view) */
  onResetCampaignProgress,
}) => {
  const mergedTodoElementVisibility = useMemo(
    () => mergeWithDefaultsForView(todoElementVisibility, 'todolist'),
    [todoElementVisibility],
  );

  const handleGoToMission = useCallback((missionId, view) => {
    window.dispatchEvent(new CustomEvent('focusMission', { detail: { missionId, view } }));
  }, []);

  // Todo toolbar add task handler - triggers modal if available
  const handleTodoAddTask = useCallback(() => {
    if (todoModalTriggerRef?.current) {
      todoModalTriggerRef.current();
    } else {
      // Fallback to direct creation
      actions.handleAddTask(null, null, 'task');
    }
  }, [todoModalTriggerRef, actions]);

  return {
    currentView: toolbar.currentView,
    onViewChange: toolbar.setCurrentView,
    flowchartRef,
    isFocusMode: toolbar.isFocusMode,
    hasSelection: toolbar.hasSelection,
    tasks,
    statuses,
    categories: actions.getAllCategories(),
    onAddTask: handlers.handleAddTaskAtViewport,
    onAddDecision: handlers.handleAddDecisionAtViewport,
    onAddWait: handlers.handleAddWaitAtViewport,
    onTodoAddTask: handleTodoAddTask,
    onFocusSelected: toolbar.handleFocusSelected,
    onExitFocusMode: toolbar.handleExitFocusMode,
    onFilterFocus: (taskIds) => flowchartRef.current?.focusTasks(taskIds, true),
    onDeleteSelected: () => flowchartRef.current?.deleteSelectedNodes(),
    onOpenArtifactSummary: () => setIsArtifactSummaryOpen(true),
    onUndo: undo,
    onRedo: redo,
    canUndo,
    canRedo,
    undoCount: undoStack.length,
    redoCount: redoStack.length,
    onToggleHistory: () => setIsHistoryOpen(!isHistoryOpen),
    onAddColumn: handleAddKanbanColumn,
    onGoToMission: handleGoToMission,
    sprintId: sprint?.id,
    cardDisplaySettingsPerView,
    onCardDisplaySettingsChange: handleCardDisplaySettingsChange,
    swimlaneSetting,
    onSwimlaneSettingChange: handleSwimlaneSettingChange,
    nestedSprints: nested.nestedSprints || [],
    swimlaneContext: {
      allTags,
      nestedSprints: nested.nestedSprints || [],
      hasPriorityField: false,
      hasAssigneeField: false,
      hasDueDateField: false,
    },
    sortSettingsPerView,
    onSortSettingChange: handleSortSettingChange,
    filtersPerView,
    onFiltersChange: handleFiltersChange,
    allTags,
    onSetWipLimit: handlers.handleSetWipLimit,
    showArchived,
    onToggleArchived: handleToggleArchived,
    archivedCount,
    showActionableOnly,
    onToggleActionableOnly: handleToggleActionableOnly,
    showNestedCampaigns,
    onShowNestedCampaignsChange: setShowNestedCampaigns,
    // Gantt swimlane setting (same as Kanban - off by default)
    ganttSwimlane,
    onGanttSwimlaneChange: setGanttSwimlane,
    ganttSwimlaneContext: {
      allTags,
      nestedSprints: nested.nestedSprints || [],
      hasPriorityField: false,
      hasAssigneeField: false,
      hasDueDateField: false,
    },
    todoListViewMode: todoViewMode,
    onTodoListViewModeChange: setTodoViewMode,
    todoListGroupBy: todoSwimlaneGroupBy,
    onTodoListGroupByChange: setTodoSwimlaneGroupBy,
    // New: Todo swimlane setting (same as Kanban)
    todoSwimlane,
    onTodoSwimlaneChange: setTodoSwimlane,
    todoSwimlaneContext: {
      allTags,
      nestedSprints: nested.nestedSprints || [],
      hasPriorityField: false,
      hasAssigneeField: false,
      hasDueDateField: false,
    },
    // New: Show nested items toggle
    todoShowNestedItems,
    onTodoShowNestedItemsChange: setTodoShowNestedItems,
    // All sprints (for nested search)
    allSprints,
    // Multi-campaign mode
    isMultiCampaignMode,
    activeView,
    multiCampaignCampaigns,
    onOpenCampaignPicker,
    // HUD visibility settings
    flowViewVisibility,
    onFlowViewVisibilityChange,
    // FOKUS mode for Kanban and Table views
    kanbanFokus,
    tableFokus,
    // FOKUS mode for Gantt, Todo and Neural views
    ganttFokus,
    todoFokus,
    neuralFokus,
    // Table column visibility
    tableColumnVisibility,
    onTableColumnVisibilityChange,
    // Todo element visibility (merged with defaults + legacy key migration)
    todoElementVisibility: mergedTodoElementVisibility,
    onTodoElementVisibilityChange,
    // Table swimlane setting (same as Kanban - off by default)
    tableSwimlane,
    onTableSwimlaneChange: setTableSwimlane,
    tableSwimlaneContext: {
      allTags,
      nestedSprints: nested.nestedSprints || [],
      hasPriorityField: false,
      hasAssigneeField: false,
      hasDueDateField: false,
    },
    // Custom views
    customViews,
    activeCustomViewId,
    onAddCustomView,
    onSelectCustomView,
    onRenameCustomView,
    onDuplicateCustomView,
    onDeleteCustomView,
    onReorderViews,
    // Custom fields (campaign-level definitions)
    customFields,
    onManageCustomFields,
    isTidyToolbarBusy,
    onResetCampaignProgress,
  };
};

export default useViewSwitcherProps;
